import { create } from 'zustand';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CartItem, CartStore } from '@/types/cart';

export const useCartStore = create<CartStore>((set, get) => ({
  items: [],
  isLoading: false,
  channel: null,

  initialize: async () => {
    try {
      set({ isLoading: true });
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('cart_items')
        .select(`
          *,
          stock_item:stock_items (
            id,
            quantity,
            data
          )
        `)
        .eq('created_by', user.id);

      if (error) throw error;

      const typedData = (data || []).map(item => ({
        ...item,
        stock_item: {
          ...item.stock_item,
          data: {
            name: item.stock_item.data.name || '',
            unit: item.stock_item.data.unit || '',
            price: Number(item.stock_item.data.price) || 0,
            status: item.stock_item.data.status || '',
            minQuantity: Number(item.stock_item.data.minQuantity) || 0
          }
        }
      })) as CartItem[];

      set({ items: typedData });

      // Setup realtime subscription
      const channel = supabase
        .channel('cart-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'cart_items'
          },
          async () => {
            // Refetch cart items when any change occurs
            const { data: newData, error: refetchError } = await supabase
              .from('cart_items')
              .select(`
                *,
                stock_item:stock_items (
                  id,
                  quantity,
                  data
                )
              `)
              .eq('created_by', user.id);

            if (refetchError) {
              console.error("Error refetching cart items:", refetchError);
              return;
            }

            const newTypedData = (newData || []).map(item => ({
              ...item,
              stock_item: {
                ...item.stock_item,
                data: {
                  name: item.stock_item.data.name || '',
                  unit: item.stock_item.data.unit || '',
                  price: Number(item.stock_item.data.price) || 0,
                  status: item.stock_item.data.status || '',
                  minQuantity: Number(item.stock_item.data.minQuantity) || 0
                }
              }
            })) as CartItem[];

            set({ items: newTypedData });
          }
        )
        .subscribe((status) => {
          console.log("Realtime subscription status:", status);
        });

      set({ channel });
    } catch (error) {
      console.error("Erreur lors de l'initialisation du panier:", error);
      toast.error("Erreur lors de l'initialisation du panier");
    } finally {
      set({ isLoading: false });
    }
  },

  addItem: async (stockItemId: string, quantity: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      // Check if item already exists in cart
      const { data: existingItems } = await supabase
        .from('cart_items')
        .select('*')
        .eq('stock_item_id', stockItemId)
        .eq('created_by', user.id);

      if (existingItems && existingItems.length > 0) {
        toast.error("Ce produit est déjà dans votre panier");
        return;
      }

      const { error } = await supabase
        .from('cart_items')
        .insert({
          stock_item_id: stockItemId,
          quantity,
          created_by: user.id
        });

      if (error) throw error;
      toast.success("Produit ajouté au panier");
    } catch (error: any) {
      console.error("Erreur lors de l'ajout au panier:", error);
      toast.error(error.message || "Erreur lors de l'ajout au panier");
    }
  },

  removeItem: async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      toast.success("Produit retiré du panier");
    } catch (error) {
      console.error("Erreur lors de la suppression du produit:", error);
      toast.error("Erreur lors de la suppression du produit");
    }
  },

  calculateTotal: () => {
    return get().items.reduce((total, item) => {
      return total + (item.quantity * item.stock_item.data.price);
    }, 0);
  },

  clearCart: () => {
    set({ items: [] });
  },

  cleanup: () => {
    const { channel } = get();
    if (channel) {
      supabase.removeChannel(channel);
    }
  }
}));